import { useState } from 'react';
import useLocalStorage from '@/hooks/useLocalStorage';
import { defaultCollections, type AdminCollection } from '@/mocks/adminData';

const emptyCollection: AdminCollection = {
  id: '',
  name: '',
  shortDescription: '',
  coverImage: '',
  active: true,
  featured: false,
};

function genId(list: AdminCollection[]): string {
  const max = list.reduce((acc, c) => { const n = parseInt(c.id.replace('col-', ''), 10); return n > acc ? n : acc; }, 0);
  return `col-${max + 1}`;
}

function CollectionsPage() {
  const [collections, setCollections] = useLocalStorage<AdminCollection[]>('admin_collections', defaultCollections);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AdminCollection | null>(null);
  const [form, setForm] = useState<AdminCollection>(emptyCollection);
  const [deleteTarget, setDeleteTarget] = useState<AdminCollection | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const openNew = () => { setEditing(null); setForm({ ...emptyCollection, id: genId(collections) }); setShowModal(true); };
  const openEdit = (c: AdminCollection) => { setEditing(c); setForm({ ...c }); setShowModal(true); };

  const handleSave = () => {
    if (!form.name.trim()) return;
    setCollections(editing ? collections.map((c) => (c.id === form.id ? form : c)) : [...collections, form]);
    setShowModal(false); setEditing(null);
    setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2000);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setCollections(collections.filter((c) => c.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {savedFlash && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium shadow-sm">
          <i className="ri-check-line mr-1.5" />Alterações salvas com sucesso.
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Coleções</h1>
          <p className="text-sm text-foreground-500 mt-1">Gerencie as coleções da loja.</p>
        </div>
        <button type="button" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap">
          <i className="ri-add-line" />Nova coleção
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {collections.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-stack-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500 mb-1">Nenhuma coleção cadastrada</p>
            <button type="button" onClick={openNew} className="text-xs text-primary-600 hover:text-primary-700 font-medium">Criar primeira coleção</button>
          </div>
        ) : (
          collections.map((col) => (
            <div key={col.id} className="bg-white rounded-xl border border-background-200 overflow-hidden hover:border-primary-200 transition-colors">
              <div className="aspect-[3/2] overflow-hidden">
                <img src={col.coverImage} alt={col.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900">{col.name}</h3>
                  <div className="flex items-center gap-1 shrink-0">
                    {col.featured && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Destaque</span>}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${col.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{col.active ? 'Ativa' : 'Inativa'}</span>
                  </div>
                </div>
                <p className="text-xs text-foreground-500 mb-3 line-clamp-2">{col.shortDescription}</p>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => openEdit(col)} className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors text-center">Editar</button>
                  <button type="button" onClick={() => setDeleteTarget(col)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors">
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-5">{editing ? 'Editar coleção' : 'Nova coleção'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Nome da coleção</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Descrição curta</label>
                <input type="text" value={form.shortDescription} onChange={(e) => setForm({ ...form, shortDescription: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">Imagem de capa (URL)</label>
                <input type="text" value={form.coverImage} onChange={(e) => setForm({ ...form, coverImage: e.target.value })} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
                {form.coverImage && <img src={form.coverImage} alt="Preview" className="mt-2 w-full h-32 rounded-lg object-cover border border-background-200" />}
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400" /><span className="text-sm text-foreground-700">Ativa</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400" /><span className="text-sm text-foreground-700">Destaque na home</span></label>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-background-100">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap">Cancelar</button>
              <button type="button" onClick={handleSave} className="px-4 py-2.5 rounded-full text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors whitespace-nowrap">Salvar alterações</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-sm p-6">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3"><i className="ri-delete-bin-line text-lg text-red-500" /></div>
            <p className="text-sm text-foreground-700 text-center mb-1">Tem certeza que deseja excluir?</p>
            <p className="text-xs text-foreground-500 text-center mb-5">{deleteTarget.name}</p>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors whitespace-nowrap">Cancelar</button>
              <button type="button" onClick={handleDelete} className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CollectionsPage;